import { useEffect, useState } from 'react';
import axios from 'axios';
import logger from '@/lib/logger';
import {
  Trophy, Lightning, Barbell, Medal, Flame, Crown, Sun, Moon, Timer,
  Wind, Handshake, Gift, Sparkle, LockKey,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  Lightning, Barbell, Medal, Trophy, Flame, Crown, Sun, Moon, Timer, Wind, Handshake, Gift, Sparkle,
};

const TIER_STYLE = {
  bronze: { ring: 'border-[#CD7F32]', text: 'text-[#CD7F32]', glow: 'shadow-[0_0_20px_rgba(205,127,50,0.25)]' },
  silver: { ring: 'border-[#C0C0C0]', text: 'text-[#C0C0C0]', glow: 'shadow-[0_0_20px_rgba(192,192,192,0.25)]' },
  gold:   { ring: 'border-[#FFD700]', text: 'text-[#FFD700]', glow: 'shadow-[0_0_24px_rgba(255,215,0,0.35)]' },
};

export default function AchievementsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | unlocked | locked

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const resp = await axios.get(`${API}/achievements/me`);
        if (alive) setData(resp.data);
      } catch (err) {
        logger.error('Achievements load err:', err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading || !data) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center" data-testid="achievements-loading">
        <div className="w-8 h-8 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { summary, badges } = data;
  const filtered = badges.filter(b => filter === 'all' || (filter === 'unlocked' ? b.unlocked : !b.unlocked));

  return (
    <div className="max-w-5xl mx-auto" data-testid="achievements-page">
      {/* Header */}
      <div className="mb-6">
        <div className="label-tactical text-[#FFD700] mb-1">Progress</div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">ACHIEVEMENTS</h1>
        <p className="text-[#A1A1AA] text-sm mt-2">Earn badges by showing up. Every workout counts.</p>
      </div>

      {/* Summary bar */}
      <div className="tactical-card p-5 mb-6" data-testid="achievements-summary">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Trophy weight="fill" className="w-5 h-5 text-[#FFD700]" />
            <h2 className="text-lg font-bold tracking-tight">Your progress</h2>
          </div>
          <span className="text-sm text-[#A1A1AA]">
            <b className="text-white text-xl font-black font-['Barlow_Condensed']">{summary.unlocked}</b>
            <span className="mx-1 text-[#71717A]">/</span>
            <span>{summary.total}</span>
            <span className="ml-2 text-xs text-[#71717A]">badges earned</span>
          </span>
        </div>
        <div className="h-3 bg-[#1C1C1E] border border-white/5 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#FFD700] via-[#FF9500] to-[#FF3B30] transition-all duration-500"
            style={{ width: `${summary.progress_pct}%` }}
            data-testid="achievements-progress-bar"
          />
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-2 mb-5" data-testid="achievements-filters">
        {[
          { key: 'all', label: `All · ${badges.length}` },
          { key: 'unlocked', label: `Earned · ${summary.unlocked}` },
          { key: 'locked', label: `Locked · ${badges.length - summary.unlocked}` },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setFilter(t.key)}
            data-testid={`filter-${t.key}`}
            className={`text-xs uppercase tracking-wider font-bold px-3 py-2 transition-colors ${
              filter === t.key
                ? 'bg-[#FF3B30] text-white'
                : 'bg-[#1C1C1E] text-[#A1A1AA] hover:text-white border border-white/5'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Badge grid */}
      {filtered.length === 0 ? (
        <div className="tactical-card p-10 text-center" data-testid="achievements-empty">
          <Trophy weight="duotone" className="w-12 h-12 text-[#71717A] mx-auto mb-3 opacity-50" />
          <p className="text-sm text-[#71717A]">Nothing here yet. Switch filter or crush a workout.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-24 lg:mb-6">
          {filtered.map(b => <BadgeCard key={b.id} badge={b} />)}
        </div>
      )}
    </div>
  );
}

function BadgeCard({ badge }) {
  const Icon = ICON_MAP[badge.icon] || Trophy;
  const tier = TIER_STYLE[badge.tier] || TIER_STYLE.bronze;
  const unlocked = badge.unlocked;

  return (
    <div
      data-testid={`badge-${badge.id}`}
      className={`tactical-card p-4 relative overflow-hidden transition-all ${
        unlocked ? `${tier.glow}` : 'opacity-60 grayscale'
      }`}
    >
      {/* Tier corner */}
      <div className={`absolute top-2 right-2 text-[10px] font-bold uppercase tracking-wider ${tier.text}`}>
        {badge.tier}
      </div>

      {/* Icon medallion */}
      <div className={`w-16 h-16 mx-auto mb-3 rounded-full border-4 ${tier.ring} flex items-center justify-center bg-[#0A0A0A] relative`}>
        {unlocked ? (
          <Icon weight="fill" className={`w-8 h-8 ${tier.text}`} />
        ) : (
          <LockKey weight="fill" className="w-6 h-6 text-[#71717A]" />
        )}
      </div>

      <div className="text-center">
        <div className="font-black tracking-tight text-sm mb-0.5">{badge.name}</div>
        <div className="text-[11px] text-[#71717A] mb-3 line-clamp-2 min-h-[28px]">{badge.desc}</div>

        {unlocked ? (
          <div className="text-[10px] text-[#34C759] font-bold uppercase tracking-wider" data-testid={`badge-${badge.id}-unlocked`}>
            ✓ Earned {badge.unlocked_at ? new Date(badge.unlocked_at).toLocaleDateString() : ''}
          </div>
        ) : (
          <>
            <div className="h-1.5 bg-[#1C1C1E] border border-white/5 overflow-hidden mb-1">
              <div
                className="h-full bg-[#FF3B30] transition-all duration-500"
                style={{ width: `${badge.progress_pct}%` }}
              />
            </div>
            <div className="text-[10px] text-[#71717A] font-mono" data-testid={`badge-${badge.id}-progress`}>
              {badge.current} / {badge.target}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
